import React, { useEffect, useMemo } from 'react';
import type { DatesSetArg, EventContentArg, EventDropArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import axios from 'axios';

import AlertBanner from '~/components/core/AlertBanner';
import Tooltip from '~/components/core/Atomic/Tooltip';
import Text from '~/components/core/TextComponents/Text';
import { useCms } from '~/components/hooks/useCms';
import ViewNotificationCard from '~/components/Notifications/ViewNotificationCard';
import Switch from '~/components/ui/atoms/Switch';
import {
  areSameLocalDate,
  compareDatesStrings,
  DATE_COMPARISON,
  getDateTimeFromUtcByUnit,
  localToServerDateTimeNoonUTC,
  serverDateTimeToLocalDate,
} from '~/DateTimeUtils';
import colors from '~/theme/tailwind/colors';
import type { CLAIM_NOTIFICATIONS_PRIORITY } from '~/Types';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isUserImpersonated } from '~/UserUtils';
import { isFeatureEnabled, isProductionEnv, reportErrorInProductionOrThrow } from '~/Utils';

import { useCalendarNotifications } from './AdjusterCalendar/hooks/useCalendarNotifications';
import type { ClaimNotificationModel } from './ClaimNotificationsCard/types';
import type { ExposureModel } from './types/exposure-types';

import { useStyles } from '../assets/styles';

// copied from https://github.com/fullcalendar/fullcalendar/blob/master/packages/common/src/common/StandardEvent.tsx
// idea came from https://stackoverflow.com/questions/57194259/react-fullcalendar-v4-tooltip
// someday this might be exported https://github.com/fullcalendar/fullcalendar/issues/5927
function renderInnerContent(innerProps: EventContentArg) {
  return (
    <div className="fc-event-main-frame">
      {innerProps.timeText && <div className="fc-event-time">{innerProps.timeText}</div>}
      <div className="fc-event-title-container">
        <div className="fc-event-title fc-sticky">{innerProps.event.title || <>&nbsp;</>}</div>
      </div>
    </div>
  );
}

const PRIORITY_KEYS = ['high', 'medium', 'low'] as const satisfies ReadonlyArray<
  keyof typeof CLAIM_NOTIFICATIONS_PRIORITY
>;
const PRIORITY_ORDER: Record<string, number> = Object.fromEntries(PRIORITY_KEYS.map((key, i) => [key, i]));
const PRIORITY_COLORS: Record<string, { bg: string; text: string }> = {
  high: { bg: colors.red[700], text: colors.white },
  medium: { bg: colors.yellow[700], text: colors.grey[900] },
  low: { bg: colors.green[700], text: colors.white },
};
const DEFAULT_PRIORITY_COLOR = { bg: colors.slate[700], text: colors.white };
const UNMAPPED_PRIORITY_SORT_ORDER = PRIORITY_KEYS.length;

function AdjusterCalendar() {
  const { classes } = useStyles();
  const calendarRef = React.useRef<FullCalendar>(null);
  const [eventsPendingUpdate, setEventsPendingUpdate] = React.useState<{ [key: string]: Date | null }>({});
  const [claimNotificationSelected, setClaimNotificationSelected] = React.useState<ClaimNotificationModel | undefined>(
    undefined
  );
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [showCommunications, setShowCommunications] = React.useState(false);
  const { user, userOrganization } = useCms();
  const [dueDateRange, setDueDateRange] = React.useState<{
    start: string;
    end: string;
  }>({
    start:
      getDateTimeFromUtcByUnit({ dateUtc: new Date().toISOString(), unitOfTime: 'months', additionInUnit: -4 }) || '',
    end: getDateTimeFromUtcByUnit({ dateUtc: new Date().toISOString(), unitOfTime: 'months', additionInUnit: 4 }) || '',
  });
  const { notifications, exposuresDict, reload, updateNotification } = useCalendarNotifications({
    dateRange: dueDateRange,
    userId: user?.id,
    organizationId: userOrganization?.id,
    should_return_future_reminders: true,
    withReadStatus: true,
  });

  const isCalendarEnhancedFilters = isFeatureEnabled(
    userOrganization,
    CONFIGURATION_FEATURES_NAMES.CALENDAR_ENHANCED_FILTERS
  );

  // Clear error message when calendar view changes
  useEffect(() => {
    setErrorMessage(null);
  }, [dueDateRange]);

  async function handleNotificationChangeDate(eventDropInfo: EventDropArg) {
    const claimNotification: ClaimNotificationModel = eventDropInfo.event.extendedProps.claimNotification;
    const start = eventDropInfo.event.start;
    if (!start) {
      return;
    }
    const creationDate = localToServerDateTimeNoonUTC(claimNotification.datetime);

    const isSameDay = areSameLocalDate(start, creationDate);
    const proposedDateStr = isSameDay ? creationDate : localToServerDateTimeNoonUTC(start);

    if (compareDatesStrings(proposedDateStr, creationDate) === DATE_COMPARISON.BEFORE) {
      setErrorMessage("A notification's due date cannot be set to a time before its creation date.");
      eventDropInfo.revert();
      return;
    }
    const notificationId = claimNotification?.id?.toString() || '';
    if (!claimNotification || !notificationId) {
      return;
    }

    const originalNotification = notifications.find((n) => n.id === claimNotification.id);
    if (!originalNotification) return;

    const optimisticNotification = { ...claimNotification, due_datetime: proposedDateStr };

    // Optimistic update
    updateNotification(optimisticNotification);
    setEventsPendingUpdate((curr) => ({ ...curr, [notificationId]: start }));
    try {
      await axios.patch(`/api/v1/notifications/${claimNotification.id}/due_date`, {
        claim_id: claimNotification.claim_id,
        due_datetime: proposedDateStr,
      });
      reload();
      setErrorMessage(null);
    } catch (error) {
      // Revert on error
      updateNotification(originalNotification);
      setErrorMessage('Failed to update the notification due date. Please try again.');
      setEventsPendingUpdate((curr) => {
        const currCpy = { ...curr };
        delete currCpy[notificationId];
        return currCpy;
      });
      eventDropInfo.revert();
      return;
    }
    setEventsPendingUpdate((curr) => {
      const currCpy = { ...curr };
      delete currCpy[notificationId];
      return currCpy;
    });
  }

  function getClaimNotificationTitle(
    claimNotification: ClaimNotificationModel,
    exposuresDict: { [x: string]: ExposureModel }
  ) {
    const exposureId = claimNotification.exposure_id?.toString() || '';
    if (!exposureId) {
      return claimNotification.claim_id_display || 'Unknown Claim';
    }

    const exposure = exposuresDict[exposureId];
    if (!exposure) {
      return claimNotification.claim_id_display || 'Unknown Claim';
    }

    const exposurePrefix = `${claimNotification.claim_id_display}-${exposure.claim_internal_id.toFixed(2)}`;

    if (exposure.involved_property) {
      return `${exposurePrefix} - ${exposure.involved_property.owner_contact_full_name}`;
    }

    if (exposure.involved_person) {
      return `${exposurePrefix} - ${exposure.involved_person.contact_full_name}`;
    }

    return exposurePrefix;
  }

  function isReminder(claimNotification: ClaimNotificationModel) {
    return claimNotification.type === 'reminder_claim_notification';
  }

  function getClaimNotificationStartDate(claimNotification: ClaimNotificationModel) {
    const notificationId = claimNotification.id?.toString() || '';
    if (eventsPendingUpdate[notificationId]) return eventsPendingUpdate[notificationId];

    return claimNotification.due_datetime ? serverDateTimeToLocalDate(claimNotification.due_datetime) : undefined;
  }

  const shouldFilterCommunications = isCalendarEnhancedFilters && !showCommunications;

  const filteredNotifications = useMemo(
    () =>
      shouldFilterCommunications
        ? notifications.filter((n) => n.type !== 'communication_claim_notification')
        : notifications,
    [notifications, shouldFilterCommunications]
  );

  const handleShowNotification = async (notificationId: number) => {
    try {
      if (isProductionEnv() && isUserImpersonated(userOrganization)) return;

      await axios.post(`/api/v1/claims/notifications/${notificationId}/mark_as_read`);
    } catch (error) {
      reportErrorInProductionOrThrow(error);
    }
  };

  const events =
    filteredNotifications.map((claimNotification) => {
      if (isCalendarEnhancedFilters) {
        const priorityKey = claimNotification.priority?.toLowerCase() ?? '';
        const colors = PRIORITY_COLORS[priorityKey] || DEFAULT_PRIORITY_COLOR;
        return {
          title: `${getClaimNotificationTitle(claimNotification, exposuresDict)} - ${claimNotification.title}`,
          start: getClaimNotificationStartDate(claimNotification) || undefined,
          allDay: true,
          claimNotification,
          textColor: eventsPendingUpdate[claimNotification.id] ? undefined : colors.text,
          backgroundColor: eventsPendingUpdate[claimNotification.id] ? 'PowderBlue' : colors.bg,
        };
      }
      return {
        title: `${getClaimNotificationTitle(claimNotification, exposuresDict)} - ${claimNotification.title}`,
        start: getClaimNotificationStartDate(claimNotification) || undefined,
        allDay: true,
        claimNotification,
        textColor: !isReminder(claimNotification) ? 'Blue' : undefined,
        backgroundColor: eventsPendingUpdate[claimNotification.id]
          ? 'PowderBlue'
          : !isReminder(claimNotification)
            ? 'White'
            : undefined,
      };
    }) || [];

  return (
    <>
      {errorMessage && (
        <AlertBanner
          note={errorMessage}
          alertType={AlertBanner.ALERT_TYPES.ERROR}
          withIcon
          className={classes.alertBanner}
        />
      )}
      {isCalendarEnhancedFilters && (
        <span className="mb-8 flex items-center px-12">
          <Switch
            checked={showCommunications}
            onChange={(e) => setShowCommunications(e.target.checked)}
            disabled={false}
          />
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
            Include communications
          </Text>
        </span>
      )}
      <div className={classes.cardDivRow} style={{ height: '100%' }}>
        <FullCalendar
          ref={calendarRef}
          initialView="dayGridMonth"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,dayGridWeek,dayGridDay',
          }}
          height="100%"
          firstDay={1}
          plugins={[dayGridPlugin, interactionPlugin]}
          editable
          eventStartEditable
          {...(isCalendarEnhancedFilters && {
            eventOrder: (a: unknown, b: unknown) => {
              const aEvent = a as { extendedProps?: { claimNotification?: ClaimNotificationModel } };
              const bEvent = b as { extendedProps?: { claimNotification?: ClaimNotificationModel } };
              const aPriority =
                PRIORITY_ORDER[aEvent.extendedProps?.claimNotification?.priority?.toLowerCase() ?? ''] ??
                UNMAPPED_PRIORITY_SORT_ORDER;
              const bPriority =
                PRIORITY_ORDER[bEvent.extendedProps?.claimNotification?.priority?.toLowerCase() ?? ''] ??
                UNMAPPED_PRIORITY_SORT_ORDER;
              return aPriority - bPriority;
            },
          })}
          eventDrop={handleNotificationChangeDate}
          eventClick={(info) => {
            const claimNotification = info.event.extendedProps.claimNotification;
            setClaimNotificationSelected(claimNotification);
            handleShowNotification(claimNotification.id);
          }}
          eventContent={(arg) => <Tooltip title={arg.event.title}>{renderInnerContent(arg)}</Tooltip>}
          dayMaxEventRows
          events={events}
          datesSet={(arg: DatesSetArg) => {
            const { startStr: startDate, endStr: endDate } = arg;
            if (!startDate || !endDate) {
              return;
            }

            // Clear error message when calendar view changes
            setErrorMessage(null);

            const startDateString = getDateTimeFromUtcByUnit({
              dateUtc: startDate,
              unitOfTime: 'months',
              additionInUnit: -2,
            });
            const endDateString = getDateTimeFromUtcByUnit({
              dateUtc: endDate,
              unitOfTime: 'months',
              additionInUnit: 2,
            });

            const isBeforeDueDateRange =
              startDateString && compareDatesStrings(startDate, dueDateRange.start) === DATE_COMPARISON.BEFORE;

            const isAfterDueDateRange =
              endDateString && compareDatesStrings(endDate, dueDateRange.end) === DATE_COMPARISON.AFTER;

            // Update state only if we need to expand the date range
            if (isBeforeDueDateRange || isAfterDueDateRange) {
              setDueDateRange({
                start: isBeforeDueDateRange ? startDateString : dueDateRange.start,
                end: isAfterDueDateRange ? endDateString : dueDateRange.end,
              });
            }
          }}
        />
      </div>
      {claimNotificationSelected && (
        <ViewNotificationCard
          notification={claimNotificationSelected}
          onUpdate={() => {
            reload();
            setClaimNotificationSelected(undefined);
          }}
          onClose={() => setClaimNotificationSelected(undefined)}
          onMarkAsUnread={() => {}}
          onUpdateNotification={() => {}}
        />
      )}
    </>
  );
}

export default AdjusterCalendar;
